/**
 * MDM list field for mapping approval cards — mirrors Create Media Plan list dropdowns
 * without using TemplateField / FieldRenderers.
 *
 * ORCH-412: retired from MaestroMappingsStepper, which now renders the form's own
 * FieldRenderers for every state. Its only remaining consumer is ApprovalCard's
 * mdm_mapping branch, which is unreachable (MaestroChatPanel filters mdm_mapping
 * gates out of ApprovalCard); delete this file together with that branch. The
 * pure helpers it used to own live in `createPlanFieldValues`.
 *
 * ORCH-376: this control *displays* Maestro's proposal but never writes it to the
 * plan form. A proposal is not an answer — only an explicit user action (picking
 * an option here, or clicking Confirm on the gate) puts a value in the plan. The
 * gate exists precisely because confidence was too low to auto-apply, so seeding
 * the form made the field look resolved while the gate said it was not, and left
 * a skipped proposal sitting in the saved plan.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import { useMDMValues } from '../../services/mdmCacheService';
import { useCreatePlanFieldLabels } from '../../hooks/useModelFieldLabels';
import type { MasterDataValue } from '../../types/mdm';
import type { ApprovalRequiredEvent } from '../../types/chat';
import { resolveMdmOption, wrapForFieldShape } from './createPlanFieldValues';

/** Merge SSE mapping.options (label/value) with MDM rows so we always have something to show while MDM loads. */
function mergeOptionsFromPayload(
  mdm: MasterDataValue[],
  mapping: NonNullable<ApprovalRequiredEvent['mapping']>
): MasterDataValue[] {
  const raw = mapping.options;
  if (!raw || !raw.length) return mdm;
  const extras: MasterDataValue[] = [];
  for (const row of raw) {
    if (!row || row.value === undefined || row.value === null) continue;
    const id = String(row.value);
    if (mdm.some((m) => String(m.id) === id)) continue;
    extras.push({
      id,
      fielddef_id: mapping.fd_key,
      tenant_id: '',
      name: String(row.label ?? id),
      is_active: true,
      is_deleted: false,
      created_at: '',
      updated_at: '',
      created_by: '',
      updated_by: '',
      scope_info: { scope: 'LOCAL', can_edit: false, can_delete: false, hierarchy_level: 0 },
    });
  }
  return [...extras, ...mdm];
}

export interface ApprovalMappingFieldProps {
  tenantId: string;
  mapping: NonNullable<ApprovalRequiredEvent['mapping']>;
  /** Current value on the Create Media Plan form (same metadata key as fd_key). */
  liveFormValue: unknown;
  disabled?: boolean;
  /** Updates the left-hand TemplateForm via applyAssistantDraft — keeps both controls in sync. */
  onApplyToPlanForm?: (fields: Record<string, unknown>) => void;
  /** Report the human-readable + stored choice for orchestrator resume. */
  onResolvedChoice: (choice: { label: string; value: unknown } | null) => void;
}

const ApprovalMappingField: React.FC<ApprovalMappingFieldProps> = ({
  tenantId,
  mapping,
  liveFormValue,
  disabled,
  onApplyToPlanForm,
  onResolvedChoice,
}) => {
  const fdKey = mapping.fd_key;
  // ORCH-381: the label comes from the same metadata view the plan form renders, never a
  // hardcoded table — the agent payload label is only a fallback while the view loads.
  const { labelFor } = useCreatePlanFieldLabels(tenantId);
  const label = labelFor(fdKey, mapping.field_label);

  const { data: mdmOptions = [], isLoading: loading } = useMDMValues(fdKey, tenantId, {}, {});

  const options = useMemo(
    () => mergeOptionsFromPayload(mdmOptions, mapping),
    [mdmOptions, mapping]
  );

  /**
   * The value shown in the dropdown: whatever the plan form already holds, else
   * Maestro's proposal as a *display-only* default (ORCH-376 — showing it is not
   * the same as applying it; nothing is written to the plan until the user acts).
   */
  const selected = useMemo(() => {
    const fromForm = resolveMdmOption(liveFormValue, options);
    if (fromForm) return fromForm;
    if (mapping.proposed_value != null && String(mapping.proposed_value).trim() !== '') {
      const fromProposal = resolveMdmOption(mapping.proposed_value, options);
      if (fromProposal) return fromProposal;
    }
    return null;
  }, [liveFormValue, mapping.proposed_value, options]);

  const onChoiceRef = useRef(onResolvedChoice);
  onChoiceRef.current = onResolvedChoice;
  useEffect(() => {
    if (selected) {
      onChoiceRef.current({ label: selected.name, value: selected.id });
    } else {
      onChoiceRef.current(null);
    }
  }, [selected]);

  if (loading && options.length === 0) {
    return (
      <Box sx={{ maxWidth: '100%', mt: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          label={label}
          value="Loading options…"
          disabled
          InputProps={{ endAdornment: <CircularProgress size={18} /> }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '100%', mt: 0.5 }}>
      <Autocomplete
        size="small"
        fullWidth
        options={options}
        getOptionLabel={(o) => o.name}
        value={selected}
        onChange={(_, opt) => {
          const v = opt?.id ?? null;
          onApplyToPlanForm?.({ [fdKey]: wrapForFieldShape(fdKey, v) });
          if (opt) {
            onResolvedChoice({ label: opt.name, value: opt.id });
          } else {
            onResolvedChoice(null);
          }
        }}
        disabled={disabled}
        isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Choose the same value as the plan form"
          />
        )}
      />
    </Box>
  );
};

export default ApprovalMappingField;
